import React, { useState } from "react";

import { useGetCustomerQuery } from "../api/customerApi";
import { Button, Dropdown, Icon } from "../components/tailwind";
import { openInOverlay } from "../utils/GlobalFunctions";
import { createHref } from "../utils/hrefUtils";
import { FindAndReplaceModal } from "./FindAndReplaceModal";

type Props = {
  /**
   * "column" (default) stacks button+description rows, matching the
   * narrower sidebar it was built for on the Translate Text page. "row"
   * lays the three controls out side by side, for spots with more room.
   * "dropdown" collapses all three into a single menu button, for spots
   * where the panel would otherwise scroll out of reach.
   */
  layout?: "column" | "row" | "dropdown";
};

// Callers gate visibility themselves -- this panel has no opinion on who
// should see it (MachineTranslateText.tsx restricts it to staff/superusers).
export const TextControlPanel: React.FC<Props> = ({ layout = "column" }) => {
  const { data: customer } = useGetCustomerQuery();
  const [findAndReplaceModalOpen, setFindAndReplaceModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const items = [
    {
      testId: "machine-translate-text-no-translate-button",
      label: "No-translate word list...",
      description:
        "Specify words or terms which should never be translated, use to preserve names or specific terminology.",
      onClick: (): void =>
        openInOverlay(createHref("noTranslateWordList", customer)),
    },
    {
      testId: "machine-translate-text-glossaries-button",
      label: "Glossaries...",
      description:
        "Specify how words and terms should be translated in a specific language pair.",
      onClick: (): void => openInOverlay(createHref("glossaries", customer)),
    },
    {
      testId: "machine-translate-text-word-replacements-button",
      label: "Word Replacements...",
      description:
        "Automatically apply find and replace rules on the final text as post-processing.",
      onClick: (): void => setFindAndReplaceModalOpen(true),
    },
  ];

  if (layout === "dropdown") {
    return (
      <>
        <Dropdown<string>
          control="button"
          content={
            <span className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap">
              Text Control
              <Icon
                name={isOpen ? "expand_less" : "expand_more"}
                className="tw-text-lg tw-leading-[1]"
              />
            </span>
          }
          value={null}
          testIdBase="text-control"
          isOpenCallback={setIsOpen}
          buttonProps={{
            size: "sm",
            variant: "secondary-alt",
            className: "tw-shrink-0",
          }}
          options={items.map((item) => ({
            key: item.testId,
            value: item.testId,
            text: item.label,
            caption: item.description,
          }))}
          onChange={(value): void => {
            items.find((item) => item.testId === value)?.onClick();
          }}
        />
        <FindAndReplaceModal
          open={findAndReplaceModalOpen}
          setOpen={setFindAndReplaceModalOpen}
        />
      </>
    );
  }

  return (
    <div>
      <h5 className="tw-text-gray-500 tw-mb-2">Text Control</h5>
      <div
        className={
          layout === "row"
            ? "tw-flex tw-flex-wrap tw-gap-6"
            : "tw-grid tw-grid-cols-[auto,_1fr] tw-items-start tw-mr-auto tw-gap-4 tw-gap-y-8 tw-text-pretty"
        }
      >
        {items.map((item) =>
          layout === "row" ? (
            <div key={item.testId} className="tw-w-56">
              <Button
                size="sm"
                content={item.label}
                variant="secondary-alt"
                data-testid={item.testId}
                onClick={item.onClick}
              />
              <p className="tw-m-0 tw-mt-2 tw-text-gray-500 tw-text-sm">
                {item.description}
              </p>
            </div>
          ) : (
            <React.Fragment key={item.testId}>
              <Button
                size="sm"
                content={item.label}
                variant="secondary-alt"
                data-testid={item.testId}
                onClick={item.onClick}
              />
              <p className="tw-m-0 tw-text-gray-500 tw-text-sm">
                {item.description}
              </p>
            </React.Fragment>
          )
        )}
      </div>
      <FindAndReplaceModal
        open={findAndReplaceModalOpen}
        setOpen={setFindAndReplaceModalOpen}
      />
    </div>
  );
};
